import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Tooltip } from '@mui/material'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { TableViewSettingsData } from '../../types/app'
import { TableRange, TableDesign } from '../../types/gotypes'
import { GridCell, platinumToGrid, getCellHierarchy, CellProvenanceInfo } from './layout'
import {
  getCellStyle,
  COEFFICIENTS,
  calculateMaxLines,
  getLineClampWrapperStyle,
  getCellWidth,
  getCellHeight,
  COLORS,
  DIMENSIONS,
} from './styles'
import { CellInfoPopover } from './CellInfoPopover'
import { toRelativeRange, resolveTableRange } from '../../utils/tableRange'

/**
 * PlatinumTable Component
 *
 * A simplified table that faithfully renders the PlatinumData grid structure
 * using raw X, Y, W, H coordinates without client-side transformations.
 *
 * The server handles all view settings (freq, col%, row%, significance) and
 * returns the appropriate PlatinumData structure.
 */

interface RangeSelectionConfig {
  onRangeChange: (range: TableRange | null) => void
  currentRange?: TableRange
}

interface PlatinumTableProps {
  data: PlatinumData;
  viewSettings?: TableViewSettingsData;
  className?: string;
  rangeSelection?: RangeSelectionConfig;
  sideLabelsMode?: 'full' | 'hide-groups' | 'hide-all';
  /**
   * Per-column pixel widths, keyed by DATA column index (#969 Part C). A
   * column with no entry keeps the default width. Kept index-keyed so this
   * component stays ignorant of the widget's column identifiers — the caller
   * resolves those (see utils/tableViewConfig.ts resolveColumnWidths).
   */
  columnWidths?: Record<number, number>;
  /**
   * Unpins the stub and the column headers. Sticky positioning is the default
   * because a crosstab that scrolls its labels away is unreadable; #969 Part B
   * surfaces this as a per-widget toggle.
   */
  disableSticky?: boolean;
  /** Table design overrides (#969 Part C) — header/cell styling, conditional formatting, significance. Passed straight to getCellStyle. */
  design?: TableDesign;
  /**
   * A cell to highlight and scroll into view.
   *
   * TWO ADDRESSING MODES, one mechanism. Citations (#1296) name the cell by its
   * row and column LABELS, matched on trimmed text ignoring case, so a citation
   * survives the table being reflowed. Provenance names it by DATA INDEX
   * (`{ rowIndex, colIndex }`) because labels are not safe to look up against:
   * duplicate labels are routine in crosstabs and Base rows shift the ordinals,
   * so a label match can land on the wrong cell while still looking right.
   *
   * A cell the table does not carry highlights nothing — the table still
   * renders normally.
   *
   * `open: true` additionally opens that cell's CellInfoPopover once layout has
   * settled. It cannot go through the click path: handleCellClick needs a live
   * DOM cell and takes its position from event.clientX/Y, while rows are
   * virtualised (overscan 20) so a distant target is not mounted yet, and inside
   * a Dialog the scroll container is mid-transition at mount.
   */
  highlightCell?: HighlightCellTarget;
  /**
   * Cells to mark in amber, by DATA INDEX like provenance: the cells behind a
   * low base or low cell note (#1647, `lowBaseReport`). The table scrolls to
   * the first one when the set changes. An index the table does not carry
   * marks nothing.
   */
  markedCells?: MarkedCell[];
}

export interface MarkedCell {
  rowIndex: number
  colIndex: number
}

/**
 * A cell to highlight, addressed either by label (citations) or by data index
 * (provenance). `open` additionally opens its CellInfoPopover.
 */
/**
 * How long the open-after-layout effect waits for a virtualised row to mount.
 * ~60 frames is about a second on a 60Hz display: comfortably longer than a
 * scroll plus an MUI Dialog transition, and short enough that a row which is
 * never coming stops being waited for.
 */
const OPEN_HIGHLIGHT_MAX_FRAMES = 60

/**
 * Centre a cell in every scroll container between it and the page, innermost
 * first: the table's own, then any dialog body around it.
 *
 * Hand-rolled rather than scrollIntoView, which also scrolls the document —
 * inside a modal that shifts the page behind it. Instant rather than smooth:
 * the popover is positioned from the cell's rect a frame later, so the scroll
 * must already have landed (and a jump needs no reduced-motion variant).
 */
function scrollCellIntoView(cell: HTMLElement): void {
  const doc = cell.ownerDocument
  const view = doc.defaultView
  if (!view) return
  for (let node = cell.parentElement; node && node !== doc.body && node !== doc.documentElement; node = node.parentElement) {
    const style = view.getComputedStyle(node)
    const scrolls = (overflow: string) => /auto|scroll/.test(overflow || style.overflow)
    const scrollsX = scrolls(style.overflowX) && node.scrollWidth > node.clientWidth
    const scrollsY = scrolls(style.overflowY) && node.scrollHeight > node.clientHeight
    if (!scrollsX && !scrollsY) continue
    const box = node.getBoundingClientRect()
    const rect = cell.getBoundingClientRect()
    if (scrollsX) node.scrollLeft += rect.left + rect.width / 2 - (box.left + box.width / 2)
    if (scrollsY) node.scrollTop += rect.top + rect.height / 2 - (box.top + box.height / 2)
  }
}

/** A cancel closure for a scheduled frame, so the effect's cleanup can hold one
 *  reference regardless of how many frames it ends up scheduling. */
const rafCancel = (handle: number) => () => cancelAnimationFrame(handle)

export type HighlightCellTarget =
  | { row: string; column: string; open?: boolean }
  | { rowIndex: number; colIndex: number; open?: boolean }

/** Resolve a highlight target to its side/top vector indices, or null.
 *
 *  Label mode is the citation path and matches on trimmed, case-insensitive
 *  text. Index mode is the provenance path and is authoritative: it is used
 *  precisely because a label lookup can be ambiguous, so it is range-checked
 *  and never falls back to a label match. */
export function resolveHighlightVectors(
  data: PlatinumData,
  cell: HighlightCellTarget | undefined,
): { sideVecIndex: number; topVecIndex: number } | null {
  if (!cell) return null
  const sideVecs = data.side?.vecs ?? []
  const topVecs = data.top?.vecs ?? []

  if ('rowIndex' in cell) {
    const { rowIndex, colIndex } = cell
    if (!Number.isInteger(rowIndex) || !Number.isInteger(colIndex)) return null
    // Out of range is ignored rather than thrown: a stale provenance reference
    // must degrade to "nothing highlighted", not to a broken table.
    if (rowIndex < 0 || rowIndex >= sideVecs.length) return null
    if (colIndex < 0 || colIndex >= topVecs.length) return null
    return { sideVecIndex: rowIndex, topVecIndex: colIndex }
  }

  const normalise = (s: string) => s.trim().toLowerCase()
  const row = normalise(cell.row)
  const column = normalise(cell.column)
  if (!row || !column) return null

  const sideVecIndex = sideVecs.findIndex(v => normalise(v.label || '') === row)
  const topVecIndex = topVecs.findIndex(v => normalise(v.label || '') === column)
  if (sideVecIndex === -1 || topVecIndex === -1) return null
  return { sideVecIndex, topVecIndex }
}

export const PlatinumTable: React.FC<PlatinumTableProps> = ({
  data,
  viewSettings,
  className = '',
  rangeSelection,
  sideLabelsMode = 'full',
  columnWidths,
  disableSticky = false,
  design,
  highlightCell,
  markedCells,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const grid = platinumToGrid(data, false) // debug = false: enable cell splitting for virtualized rendering

  // Disable tooltips while scrolling so they don't float away from their cells
  const [isScrolling, setIsScrolling] = useState(false)
  const handleScroll = () => {
    if (!isScrolling) setIsScrolling(true)
    clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150)
  }

  // State for cell info popover
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null)
  const [provenance, setProvenance] = useState<CellProvenanceInfo | null>(null)
  const [clickedCell, setClickedCell] = useState<{ rowIdx: number; colIdx: number } | null>(null)
  const [clickedCellRect, setClickedCellRect] = useState<DOMRect | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  // Extract coefficients from view settings
  const horizontalCoeff = viewSettings?.horizontal_coefficient ?? COEFFICIENTS.HORIZONTAL_DEFAULT
  const verticalCoeff = viewSettings?.vertical_coefficient ?? COEFFICIENTS.VERTICAL_DEFAULT

  // Calculate header dimensions
  const { headerRows, headerCols } = useMemo(() => {
    const props = data.properties
    const topGroups = data.top?.groups
    const sideGroups = data.side?.groups

    // Top header rows
    const topThickness = topGroups && topGroups.length > 0
      ? Math.max(...topGroups.map(g => g.y ?? 0), 0) + 1
      : 0
    const topLabelRows = props?.top?.labels?.visible ? 1 : 0
    const topLetterRows = props?.significance?.letters === 'Column' ? 1 : 0
    const headerRows = topThickness + topLabelRows + topLetterRows

    // Side header columns
    const sideThickness = sideGroups && sideGroups.length > 0
      ? Math.max(...sideGroups.map(g => g.x ?? 0), 0) + 1
      : 0
    const sideLabelCols = props?.side?.labels?.visible ? 1 : 0
    const sideLetterCols = props?.significance?.letters === 'Row' ? 1 : 0
    const headerCols = sideThickness + sideLabelCols + sideLetterCols

    return { headerRows, headerCols }
  }, [data])

  // --- Range selection state & helpers ---
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null)
  const [dragEnd, setDragEnd] = useState<{ row: number; col: number } | null>(null)

  const totalDataRows = grid.length - headerRows
  const totalDataCols = (grid[0]?.length ?? 0) - headerCols

  const currentSelection = useMemo(() => {
    if (!rangeSelection?.currentRange || totalDataRows === 0 || totalDataCols === 0) return null
    return resolveTableRange(rangeSelection.currentRange, totalDataRows, totalDataCols)
  }, [rangeSelection?.currentRange, totalDataRows, totalDataCols])

  const activeSelection = useMemo(() => {
    if (!dragStart || !dragEnd) return currentSelection
    const rowStart = Math.min(dragStart.row, dragEnd.row)
    const rowEnd = Math.max(dragStart.row, dragEnd.row)
    const colStart = Math.min(dragStart.col, dragEnd.col)
    const colEnd = Math.max(dragStart.col, dragEnd.col)
    return { rowStart, rowEnd, colStart, colEnd }
  }, [dragStart, dragEnd, currentSelection])

  const isDataCell = (rowIdx: number, colIdx: number) =>
    rowIdx >= headerRows && colIdx >= headerCols

  const toDataCoords = (rowIdx: number, colIdx: number) =>
    ({ row: rowIdx - headerRows, col: colIdx - headerCols })

  const isCellInSelection = (rowIdx: number, colIdx: number) => {
    if (!activeSelection || !isDataCell(rowIdx, colIdx)) return false
    const { row, col } = toDataCoords(rowIdx, colIdx)
    return (
      row >= activeSelection.rowStart &&
      row <= activeSelection.rowEnd &&
      col >= activeSelection.colStart &&
      col <= activeSelection.colEnd
    )
  }

  const handleRangeMouseDown = useCallback((e: React.MouseEvent, rowIdx: number, colIdx: number) => {
    if (!rangeSelection || !isDataCell(rowIdx, colIdx)) return
    e.preventDefault()
    const { row, col } = toDataCoords(rowIdx, colIdx)
    setIsDragging(true)
    setDragStart({ row, col })
    setDragEnd({ row, col })
  }, [rangeSelection, headerRows, headerCols])

  const handleRangeMouseMove = useCallback((e: React.MouseEvent, rowIdx: number, colIdx: number) => {
    if (!isDragging || !isDataCell(rowIdx, colIdx)) return
    e.preventDefault()
    const { row, col } = toDataCoords(rowIdx, colIdx)
    setDragEnd({ row, col })
  }, [isDragging, headerRows, headerCols])

  const handleRangeMouseUp = useCallback(() => {
    if (!isDragging || !dragStart || !dragEnd || !rangeSelection) {
      setIsDragging(false)
      return
    }
    const rowStart = Math.min(dragStart.row, dragEnd.row)
    const rowEnd = Math.max(dragStart.row, dragEnd.row)
    const colStart = Math.min(dragStart.col, dragEnd.col)
    const colEnd = Math.max(dragStart.col, dragEnd.col)

    const { start: rowStartCoord, end: rowEndCoord } = toRelativeRange(rowStart, rowEnd, totalDataRows)
    const { start: colStartCoord, end: colEndCoord } = toRelativeRange(colStart, colEnd, totalDataCols)

    rangeSelection.onRangeChange({
      row_start: rowStartCoord,
      row_end: rowEndCoord,
      col_start: colStartCoord,
      col_end: colEndCoord,
    })
    setIsDragging(false)
    setDragStart(null)
    setDragEnd(null)
  }, [isDragging, dragStart, dragEnd, totalDataRows, totalDataCols, rangeSelection])

  // Determine which side header columns contain groups vs labels
  const sideColumnTypes = useMemo(() => {
    const types: Record<number, 'group' | 'label' | 'other'> = {}
    for (let colIdx = 0; colIdx < headerCols; colIdx++) {
      // Scan down the column to find first non-corner, non-empty side header cell
      let colType: 'group' | 'label' | 'other' = 'other'
      for (let rowIdx = 0; rowIdx < grid.length; rowIdx++) {
        const c = grid[rowIdx]?.[colIdx]
        if (c && c.position?.isSideHeader && c.content.trim() !== '') {
          colType = c.type === 'group' ? 'group' : c.type === 'label' ? 'label' : 'other'
          break
        }
      }
      types[colIdx] = colType
    }
    return types
  }, [grid, headerCols])

  // Calculate cumulative left offsets for side header columns
  const columnLeftOffsets = useMemo(() => {
    const offsets: Record<number, number> = {}
    let cumulativeLeft = 0

    for (let colIdx = 0; colIdx < headerCols; colIdx++) {
      offsets[colIdx] = cumulativeLeft
      const cell = grid[0]?.[colIdx]
      if (cell) {
        const colType = sideColumnTypes[colIdx]
        const isCompact =
          (sideLabelsMode === 'hide-groups' && colType === 'group') ||
          (sideLabelsMode === 'hide-all')
        cumulativeLeft += getCellWidth(cell, horizontalCoeff, isCompact)
      }
    }

    return offsets
  }, [grid, headerCols, horizontalCoeff, sideLabelsMode, sideColumnTypes])

  // Calculate cumulative top offsets for header rows (per-row height calculation)
  const rowTopOffsets = useMemo(() => {
    const offsets: Record<number, number> = {}
    let cumulativeTop = 0

    for (let rowIdx = 0; rowIdx < headerRows; rowIdx++) {
      offsets[rowIdx] = cumulativeTop
      // Get the first cell in this row to determine height
      const cell = grid[rowIdx]?.[0]
      if (cell) {
        cumulativeTop += getCellHeight(cell, verticalCoeff)
      }
    }

    return offsets
  }, [grid, headerRows, verticalCoeff])

  // --- Cited-cell highlight (#1296) ---
  // Resolved through the data cells' own hierarchy (which side/top vector they
  // belong to) rather than grid coordinates, so it lands on every band of the
  // cited cell and is unaffected by header thickness or cell splitting.
  const highlightVectors = useMemo(() => resolveHighlightVectors(data, highlightCell), [data, highlightCell])

  /** The vec-index key the open-after-layout effect looks the cell up by.
   *
   *  This used to also stash the cell's GRID coordinates in a ref, which is a
   *  rules-of-React violation — writing a ref during render, which the React
   *  Compiler refuses to compile past. The effect now reads the coordinates back
   *  off the DOM node it has to find anyway (data-grid-row / data-grid-col),
   *  so nothing is written during render. */
  const highlightKeyFor = (cell: GridCell): string | undefined => {
    if (!isHighlightedCell(cell)) return undefined
    return `${highlightVectors!.sideVecIndex},${highlightVectors!.topVecIndex}`
  }

  const isHighlightedCell = (cell: GridCell): boolean => (
    highlightVectors !== null &&
    cell.type === 'data' &&
    cell.hierarchy?.sideVecIndex === highlightVectors.sideVecIndex &&
    cell.hierarchy?.topVecIndex === highlightVectors.topVecIndex
  )

  // Marked cells (#1647): every band of each cell, by the data cell's own
  // hierarchy, the same way the cited cell is found.
  const markedKeys = useMemo(
    () => new Set((markedCells ?? []).map(c => `${c.rowIndex},${c.colIndex}`)),
    [markedCells],
  )
  const isMarkedCell = (cell: GridCell): boolean => (
    markedKeys.size > 0 &&
    cell.type === 'data' &&
    markedKeys.has(`${cell.hierarchy?.sideVecIndex},${cell.hierarchy?.topVecIndex}`)
  )

  // Separate grid into regions
  const headerRowsData = useMemo(() => grid.slice(0, headerRows), [grid, headerRows])
  const dataRows = useMemo(() => grid.slice(headerRows), [grid, headerRows])

  // Set up row virtualizer for data rows (columns are not virtualized)
  // Note: Hook must be called unconditionally, but we only use results when significance is disabled
  const rowVirtualizer = useVirtualizer({
    count: dataRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (index) => {
      const actualRowIdx = headerRows + index
      const cell = grid[actualRowIdx]?.[0]
      return cell ? getCellHeight(cell, verticalCoeff) : 32
    },
    overscan: 20,
  })

  // Bring the cited cell into view once, when the citation that asked for it
  // changes. Imperative and virtualiser-owned, so it belongs in an effect;
  // `dataRows` is deliberately not a dependency — a re-layout of the same
  // table must not yank the reader's scroll position back.
  const highlightRowIndex = useMemo(() => {
    if (!highlightVectors) return -1
    return dataRows.findIndex(row => row.some(cell => isHighlightedCell(cell)))
  }, [dataRows, highlightVectors])
  const scrolledToHighlight = useRef<number | null>(null)
  useEffect(() => {
    if (highlightRowIndex < 0) return
    if (scrolledToHighlight.current === highlightRowIndex) return
    scrolledToHighlight.current = highlightRowIndex
    rowVirtualizer.scrollToIndex(highlightRowIndex, { align: 'center' })
  }, [highlightRowIndex])

  // Bring the highlighted cell into view — sideways as well as down, since a
  // wide months-on-top table put the cited cell far off to the right — then
  // open its popover when asked to (provenance does; a citation does not).
  //
  // This deliberately does NOT go through handleCellClick, which needs a live
  // DOM cell and derives its position from event.clientX/Y. The target row may
  // not be mounted yet — it is virtualised, and the scroll above has only just
  // been requested — and inside an MUI Dialog the scroll container is still
  // transitioning at mount. So it waits for layout, then positions from the
  // cell's own bounding rect rather than from a mouse that was never there.
  const settledHighlight = useRef<string | null>(null)
  useEffect(() => {
    if (!highlightVectors) return
    const key = `${highlightVectors.sideVecIndex},${highlightVectors.topVecIndex}`
    const wantOpen = Boolean(highlightCell?.open)
    // Keyed on `open` too: the same cell asked for again WITH a popover must
    // still get one after an earlier scroll-only visit.
    const settledKey = `${key}:${wantOpen}`
    if (settledHighlight.current === settledKey) return

    let cancelled = false
    let frame = 0
    // Wait for the row to actually MOUNT, rather than for a fixed number of
    // frames. This used to be exactly two: one to let the virtualiser mount the
    // scrolled-to row, one to let layout settle. Two is enough for a row already
    // near the top and not enough for anything else — a cited row far down a
    // virtualised list mounts when the scroll lands, which depends on the list
    // length, the machine, and whether an MUI Dialog is still transitioning
    // around it. Missing that window opened the source table with no popover
    // and no highlight, which reads as broken rather than slow.
    //
    // So poll by frame until the cell is both present and laid out, up to a
    // deadline. Bounded, because a cell that never arrives must not leave a
    // callback running for the life of the table.
    const find = () =>
      scrollContainerRef.current?.querySelector<HTMLTableCellElement>(`[data-highlight-cell="${key}"]`) ?? null

    // Once the cell appears, give layout ONE more frame before measuring it —
    // the same settling the old second frame provided.
    const open = () => {
      if (cancelled) return
      const el = find()
      if (!el) return
      const rect = el.getBoundingClientRect()
      const rowIdx = Number(el.dataset.gridRow)
      const colIdx = Number(el.dataset.gridCol)
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return
      const cellProvenance = getCellHierarchy(grid, rowIdx, colIdx, data)
      if (!cellProvenance) return
      settledHighlight.current = settledKey
      setProvenance(cellProvenance)
      setPopoverPosition({ top: rect.bottom, left: rect.left + rect.width / 2 })
      setClickedCell({ rowIdx, colIdx })
      setClickedCellRect(rect)
      setPopoverOpen(true)
    }

    const attempt = () => {
      if (cancelled) return
      const el = find()
      if (el) {
        // Scrolled BEFORE the popover measures the cell a frame later.
        scrollCellIntoView(el)
        if (!wantOpen) {
          settledHighlight.current = settledKey
          return
        }
        cleanupRef.current = rafCancel(requestAnimationFrame(open))
        return
      }
      if (frame++ < OPEN_HIGHLIGHT_MAX_FRAMES) {
        cleanupRef.current = rafCancel(requestAnimationFrame(attempt))
      }
    }

    const first = requestAnimationFrame(attempt)
    return () => {
      cancelled = true
      cancelAnimationFrame(first)
      cleanupRef.current?.()
    }
  }, [highlightCell?.open, highlightVectors, highlightRowIndex])

  // Bring the first marked cell into view when the marked set changes: the
  // virtualiser scrolls its row in, then the cell is centred sideways once it
  // has mounted. Keyed on the set's contents, not on `dataRows`, for the same
  // reason as the cited cell: a re-layout must not move the reader.
  const markedSignature = [...markedKeys].join(';')
  const firstMarkedRowIndex = markedKeys.size > 0 ? dataRows.findIndex(row => row.some(isMarkedCell)) : -1
  useEffect(() => {
    if (firstMarkedRowIndex < 0) return
    rowVirtualizer.scrollToIndex(firstMarkedRowIndex, { align: 'center' })
    let frame = 0
    let handle = 0
    const attempt = () => {
      const el = scrollContainerRef.current?.querySelector<HTMLElement>('[data-marked-cell="true"]')
      if (el) {
        scrollCellIntoView(el)
        return
      }
      if (frame++ < OPEN_HIGHLIGHT_MAX_FRAMES) handle = requestAnimationFrame(attempt)
    }
    handle = requestAnimationFrame(attempt)
    return () => cancelAnimationFrame(handle)
  }, [markedSignature])

  // Helper to determine if a cell needs line-clamp
  const needsLineClamp = (cell: GridCell): boolean => {
    return (
      (cell.type === 'group' || cell.type === 'label') &&
      ((cell.position?.isSideHeader ?? false) || (cell.position?.isTopHeader ?? false))
    )
  }

  // Helper to render cell content with optional line-clamp wrapper and tooltip
  const renderCellContent = (cell: GridCell): React.ReactNode => {
    const content = cell.content || '\u00A0'

    // Compact side labels based on mode — uses column-level type detection
    if (cell.position?.isSideHeader && cell.content.trim() !== '' && cell.position.colIdx != null) {
      const colType = sideColumnTypes[cell.position.colIdx]
      const shouldCollapse =
        (sideLabelsMode === 'hide-groups' && colType === 'group') ||
        (sideLabelsMode === 'hide-all')
      if (shouldCollapse) {
        return (
          <Tooltip title={cell.content} placement="right" arrow>
            <span style={{ display: 'block', width: '100%', height: '100%', cursor: 'default' }}>&nbsp;</span>
          </Tooltip>
        )
      }
    }

    // Add tooltips to group and label cells (disabled while scrolling)
    const shouldShowTooltip = !isScrolling && (cell.type === 'group' || cell.type === 'label') && cell.content.trim() !== ''

    if (needsLineClamp(cell)) {
      const maxLines = calculateMaxLines(cell, verticalCoeff)
      const wrapperStyle = getLineClampWrapperStyle(maxLines)

      const wrappedContent = <span style={wrapperStyle}>{content}</span>

      if (shouldShowTooltip) {
        return (
          <Tooltip title={cell.content} placement="top" arrow>
            {wrappedContent}
          </Tooltip>
        )
      }

      return wrappedContent
    }

    // For group and label cells that don't need line-clamp (e.g., top headers)
    if (shouldShowTooltip) {
      return (
        <Tooltip title={cell.content} placement="top" arrow>
          <span>{content}</span>
        </Tooltip>
      )
    }

    return content
  }

  // Handle cell click to show popover
  const handleCellClick = (
    event: React.MouseEvent<HTMLTableCellElement>,
    cell: GridCell,
    rowIdx: number,
    colIdx: number
  ) => {
    // Only handle data cells
    if (cell.type !== 'data') return

    // Get cell hierarchy information
    const cellProvenance = getCellHierarchy(grid, rowIdx, colIdx, data)
    if (!cellProvenance) return

    // Get the clicked cell's bounding rectangle for positioning
    const cellElement = event.currentTarget
    const cellRect = cellElement.getBoundingClientRect()

    // Set popover state
    setProvenance(cellProvenance)
    setPopoverPosition({
      top: event.clientY,
      left: event.clientX
    })
    setClickedCell({ rowIdx, colIdx })
    setClickedCellRect(cellRect)
    setPopoverOpen(true)
  }

  /**
   * The saved width for a cell, or undefined to keep the computed one.
   *
   * A cell can span several data columns (a top group over three waves), so an
   * override on any one of them makes the whole span explicit — otherwise the
   * group header and the columns beneath it disagree and the table shears.
   */
  const explicitWidthFor = (cell: GridCell, colIdx: number): number | undefined => {
    if (!columnWidths || colIdx < headerCols) return undefined
    const first = colIdx - headerCols
    const span = Math.max(1, cell.colspan)
    let touched = false
    let total = 0
    for (let i = first; i < first + span; i++) {
      const width = columnWidths[i]
      if (width) touched = true
      total += width || DIMENSIONS.DATA_CELL_WIDTH
    }
    return touched ? total : undefined
  }

  // Helper to render a single cell
  const renderCell = (cell: GridCell, rowIdx: number, colIdx: number): React.ReactNode => {
    if (cell.type === 'occupied') {
      return null
    }

    const isOddRow = rowIdx % 2 === 1
    const leftOffset = colIdx < headerCols ? columnLeftOffsets[colIdx] : undefined
    const topOffset = rowIdx < headerRows ? rowTopOffsets[rowIdx] : undefined

    const cellStyle = getCellStyle(cell, {
      grid,
      horizontalCoefficient: horizontalCoeff,
      verticalCoefficient: verticalCoeff,
      isOddRow,
      leftOffset,
      topOffset,
      disableSticky,
      width: explicitWidthFor(cell, colIdx),
      compactSideLabels: cell.position?.isSideHeader ? (
        (sideLabelsMode === 'hide-groups' && sideColumnTypes[colIdx] === 'group') ||
        (sideLabelsMode === 'hide-all')
      ) : false,
      design,
    })

    // The cited cell (#1296): a soft info-blue wash and a 2px outline, so the
    // reader lands on the number the text quoted.
    // A marked cell (#1647): the same shape in the warning amber of the note
    // that asked for it. A cited cell on top of it keeps its blue.
    const highlightStyle: React.CSSProperties = isHighlightedCell(cell)
      ? { backgroundColor: 'rgba(2, 136, 209, 0.16)', outline: '2px solid #0288D1', outlineOffset: '-1px' }
      : isMarkedCell(cell)
        ? { backgroundColor: 'rgba(237, 108, 2, 0.16)', outline: '2px solid #ED6C02', outlineOffset: '-1px' }
        : {}

    // --- Range selection mode: highlight + drag events ---
    if (rangeSelection) {
      const selected = isCellInSelection(rowIdx, colIdx)
      const cursor = isDataCell(rowIdx, colIdx) ? 'crosshair' : undefined

      const selectionStyle: React.CSSProperties = selected
        ? { backgroundColor: 'rgba(33, 150, 243, 0.2)', outline: '2px solid rgba(33, 150, 243, 0.6)', outlineOffset: '-1px' }
        : {}

      return (
        <td
          key={colIdx}
          rowSpan={cell.rowspan}
          colSpan={cell.colspan}
          style={{ ...cellStyle, cursor, ...highlightStyle, ...selectionStyle }}
          data-cited-cell={isHighlightedCell(cell) ? 'true' : undefined}
          data-marked-cell={isMarkedCell(cell) ? 'true' : undefined}
          data-highlight-cell={highlightKeyFor(cell)}
          data-grid-row={isHighlightedCell(cell) ? rowIdx : undefined}
          data-grid-col={isHighlightedCell(cell) ? colIdx : undefined}
          onMouseDown={(e) => handleRangeMouseDown(e, rowIdx, colIdx)}
          onMouseMove={(e) => handleRangeMouseMove(e, rowIdx, colIdx)}
        >
          {renderCellContent(cell)}
        </td>
      )
    }

    // --- Normal (popover) mode ---

    // Calculate opacity based on clicked cell (span-aware)
    let opacity = 1.0
    let isSameRow = false
    let isSameCol = false

    if (clickedCell) {
      // Check if this cell's span includes the clicked cell's position
      const cellStartRow = rowIdx
      const cellEndRow = rowIdx + cell.rowspan
      const cellStartCol = colIdx
      const cellEndCol = colIdx + cell.colspan

      // Span-aware row check: clicked row falls within this cell's row span
      isSameRow = clickedCell.rowIdx >= cellStartRow && clickedCell.rowIdx < cellEndRow

      // Span-aware column check: clicked column falls within this cell's column span
      isSameCol = clickedCell.colIdx >= cellStartCol && clickedCell.colIdx < cellEndCol

      // Check if this is a header cell that spans into the clicked cell's row
      const isClickedRowHeader = colIdx < headerCols && isSameRow

      // Check if this is a header cell that spans into the clicked cell's column
      const isClickedColHeader = rowIdx < headerRows && isSameCol

      // Keep full opacity for same row, same column, or headers of clicked cell
      if (isSameRow || isSameCol || isClickedRowHeader || isClickedColHeader) {
        opacity = 1.0
      } else {
        opacity = 0.2
      }
    }

    // Add cursor pointer for data cells
    const cursor = cell.type === 'data' ? 'pointer' : undefined

    // Add borders for cells in active row/column (including headers)
    const borders: React.CSSProperties = {}
    if (clickedCell) {
      const isHeader = cell.type === 'group' || cell.type === 'label' || cell.type === 'letter'
      const isData = cell.type === 'data'
      const isTopHeader = rowIdx < headerRows
      const isSideHeader = colIdx < headerCols

      // Top headers: only get borders if in same column
      if(isHeader) {
        if (isTopHeader && isSameCol) {
          borders.borderLeft = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderRight = `1px solid ${COLORS.BORDER_DARK}`
        }
        if (isSideHeader && isSameRow) {
          borders.borderTop = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderBottom = `1px solid ${COLORS.BORDER_DARK}`
        }
      } else if(isData) {
        if(isSameCol && isSameRow) {
          borders.borderLeft = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderRight = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderTop = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderBottom = `1px solid ${COLORS.BORDER_DARK}`
          cellStyle.fontWeight = `bold`
        }
        else if (isSameCol) {
          // In same column but different row: left/right borders only
          borders.borderLeft = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderRight = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderBottom = ``
          borders.borderTop = ``
        }
        else if (isSameRow) {
          // In same row but different column: top/bottom borders only
          borders.borderTop = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderBottom = `1px solid ${COLORS.BORDER_DARK}`
          borders.borderLeft = ``
          borders.borderRight = ``
        }
      }
    }

    return (
      <td
        key={colIdx}
        rowSpan={cell.rowspan}
        colSpan={cell.colspan}
        style={{ ...cellStyle, cursor, opacity, ...borders, ...highlightStyle }}
        data-cited-cell={isHighlightedCell(cell) ? 'true' : undefined}
        data-marked-cell={isMarkedCell(cell) ? 'true' : undefined}
        data-highlight-cell={highlightKeyFor(cell)}
        data-grid-row={isHighlightedCell(cell) ? rowIdx : undefined}
        data-grid-col={isHighlightedCell(cell) ? colIdx : undefined}
        onClick={(e) => handleCellClick(e, cell, rowIdx, colIdx)}
      >
        {renderCellContent(cell)}
      </td>
    )
  }

  // Get virtualization data (only used when significance is disabled)
  const virtualRows = rowVirtualizer.getVirtualItems()

  // Calculate padding for row virtualization (only when enabled)
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom = virtualRows.length > 0
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0

  return (
    <div
      ref={scrollContainerRef}
      className={className}
      onScroll={handleScroll}
      onMouseUp={rangeSelection ? handleRangeMouseUp : undefined}
      onMouseLeave={rangeSelection ? handleRangeMouseUp : undefined}
      style={{
        overflow: 'auto',
        width: '100%',
        height: '100%',
        backgroundColor: '#f8f9fa',
        // No borderTop — the controls bar above provides its borderBottom
        ...(rangeSelection && isDragging ? { userSelect: 'none', cursor: 'crosshair' } as React.CSSProperties : {}),
      }}
    >
      <table style={{
        borderCollapse: 'collapse',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: '12px',
        backgroundColor: 'white',
        tableLayout: 'fixed'
      }}>
        {/* Define column widths explicitly for fixed table layout */}
        <colgroup>
          {grid[0]?.map((cell, colIdx) => {
            const width = getCellWidth(cell, horizontalCoeff)
            return <col key={colIdx} style={{ width: `${width}px` }} />
          })}
        </colgroup>

        {/* Header rows (not virtualized vertically or horizontally) */}
        <thead>
          {headerRowsData.map((row, rowIdx) => (
            <tr key={`header-${rowIdx}`}>
              {/* Render side header cells (not virtualized) */}
              {row.slice(0, headerCols).map((cell, colIdx) => renderCell(cell, rowIdx, colIdx))}

              {/* Render all top header cells (not virtualized to preserve rowspan/colspan) */}
              {row.slice(headerCols).map((cell, idx) => {
                const colIdx = headerCols + idx
                return renderCell(cell, rowIdx, colIdx)
              })}
            </tr>
          ))}
        </thead>

        {/* Data rows (virtualized when significance is disabled, static when enabled) */}
        <tbody>

          {/* Top padding for vertical virtualization */}
          {paddingTop > 0 && (
            <tr key="padding-top">
              <td style={{ height: `${paddingTop}px`, padding: 0, border: 'none' }} />
            </tr>
          )}

          {/* Render virtualized data rows */}
          {virtualRows.map((virtualRow) => {
            const rowIdx = headerRows + virtualRow.index
            const row = dataRows[virtualRow.index]

            return (
              <tr key={`data-${virtualRow.index}`}>
                {/* Render all cells in the row (no horizontal virtualization) */}
                {row.map((cell, colIdx) => renderCell(cell, rowIdx, colIdx))}
              </tr>
            )
          })}

          {/* Bottom padding for vertical virtualization */}
          {paddingBottom > 0 && (
            <tr key="padding-bottom">
              <td style={{ height: `${paddingBottom}px`, padding: 0, border: 'none' }} />
            </tr>
          )}
        
        </tbody>
      </table>
      {!rangeSelection && (
        <CellInfoPopover
          open={popoverOpen}
          position={popoverPosition}
          provenance={provenance}
          properties={data.properties}
          clickedCellRect={clickedCellRect}
          onClose={() => {
            setPopoverOpen(false)
            setClickedCell(null)
            setClickedCellRect(null)
          }}
        />
      )}
    </div>
  )
}

export default PlatinumTable
